import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io, { Socket } from 'socket.io-client';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  lastPing: number;
  emit: (event: string, data?: any) => void;
  subscribe: (event: string, callback: (data: any) => void) => () => void;
  sendAnalysisRequest: (projectPath: string) => void;
  sendRecommendationsRequest: (projectPath: string) => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  connect: () => void;
  disconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: React.ReactNode;
  url?: string;
  autoConnect?: boolean;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ 
  children, 
  url = process.env['REACT_APP_WS_URL'] || '',
  autoConnect = false
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastPing, setLastPing] = useState(Date.now());
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const urlRef = useRef(url);
  urlRef.current = url;

  const setupSocket = useCallback((): Socket => {
    const s = io(urlRef.current, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      autoConnect: false
    });

    s.on('connect', () => {
      console.log('✅ WebSocket connected successfully!');
      setIsConnected(true);
      setConnectionStatus('connected');
      setLastPing(Date.now());
    });

    s.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
      setIsConnected(false);
      setConnectionStatus('disconnected');
    });

    s.on('connect_error', (error) => {
      console.error('🔥 WebSocket connection error:', error);
      setConnectionStatus('error');
    });

    s.on('pong', () => {
      setLastPing(Date.now());
    });

    // Handle analysis progress updates
    s.on('analysis_progress', (data) => {
      console.log('📊 Analysis progress:', data);
    });

    // Handle analysis complete
    s.on('analysis_complete', (data) => {
      console.log('✅ Analysis complete:', data);
    });

    // Handle recommendations updates
    s.on('recommendations_update', (data) => {
      console.log('💡 Recommendations update:', data);
    });

    // Handle system notifications
    s.on('system_notification', (data) => {
      console.log('🔔 System notification:', data);
    });

    setSocket(s);
    return s;
  }, []);

  // Maintain a ping interval when a socket exists
  useEffect(() => {
    const interval = setInterval(() => {
      if (socket && socket.connected) {
        socket.emit('ping');
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [socket]);

  const connect = useCallback(() => {
    if (!urlRef.current) {
      console.info('ℹ️ WebSocket URL not configured; skipping connect');
      setConnectionStatus('disconnected');
      return;
    }
    console.log('🔌 Requesting WebSocket connection to:', urlRef.current);
    setConnectionStatus('connecting');
    const s = socket ?? setupSocket();
    if (!s.connected) {
      s.connect();
    }
  }, [socket, setupSocket]);

  const disconnect = useCallback(() => {
    if (socket) {
      console.log('🔌 Disconnecting WebSocket...');
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setConnectionStatus('disconnected');
    }
  }, [socket]);

  // Auto-connect only if explicitly requested
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => {
      // Ensure clean disconnect on provider unmount
      if (socket) {
        socket.disconnect();
      }
    };
  }, [autoConnect, connect]);

  const emit = useCallback((event: string, data?: any) => {
    if (socket && isConnected) {
      console.log(`📤 Emitting ${event}:`, data);
      socket.emit(event, data);
    } else {
      console.warn(`⚠️ Cannot emit ${event}: WebSocket not connected`);
    }
  }, [socket, isConnected]);

  const subscribe = useCallback((event: string, callback: (data: any) => void) => {
    if (!socket) {
      console.warn(`⚠️ Cannot subscribe to ${event}: WebSocket not available`);
      return () => {};
    }

    console.log(`📥 Subscribing to ${event}`);
    socket.on(event, callback);

    return () => {
      console.log(`📤 Unsubscribing from ${event}`);
      socket.off(event, callback);
    };
  }, [socket]);

  const sendAnalysisRequest = useCallback((projectPath: string) => {
    emit('start_analysis', { project_path: projectPath, timestamp: Date.now() });
  }, [emit]);

  const sendRecommendationsRequest = useCallback((projectPath: string) => {
    emit('get_recommendations', { project_path: projectPath, timestamp: Date.now() });
  }, [emit]);

  const contextValue: WebSocketContextType = {
    socket,
    isConnected,
    lastPing,
    emit,
    subscribe,
    sendAnalysisRequest,
    sendRecommendationsRequest,
    connectionStatus,
    connect,
    disconnect
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketProvider;